// agentic-backend's per-member connections routes (`target: 'workflow'`).
// See src/api/v1/routers/personal_connections.py: every route is scoped to
// the signed-in caller.
import { apiRequest } from '@/utils/api-request';

import {
  ConnectResult,
  DisconnectResult,
  PersonalConnection,
  PersonalConnectionsResponse,
  TestResult,
} from './types';

const base = (orgId: string) => `/api/v1/orgs/${orgId}/personal-connections`;

const failed = (response: { detail?: string } | undefined, fallback: string) =>
  new Error(response?.detail || fallback);

export const listPersonalConnections = async (
  orgId: string,
): Promise<PersonalConnectionsResponse> => {
  const response = await apiRequest<PersonalConnectionsResponse>({
    url: base(orgId),
    method: 'GET',
    target: 'workflow',
  });
  if (response?.detail || !response?.data) {
    throw failed(response, 'Could not load your connections');
  }
  return response.data;
};

export const startPersonalConnection = async (
  orgId: string,
  provider: string,
): Promise<ConnectResult> => {
  const response = await apiRequest<ConnectResult>({
    url: `${base(orgId)}/${provider}/connect`,
    method: 'POST',
    target: 'workflow',
    body: { popup: true },
  });
  if (response?.detail || !response?.data) {
    throw failed(response, 'Could not start the sign-in');
  }
  return response.data;
};

export const completePersonalConnection = async (
  orgId: string,
  provider: string,
  code: string,
  state: string,
): Promise<PersonalConnection> => {
  const response = await apiRequest<{ connection: PersonalConnection }>({
    url: `${base(orgId)}/${provider}/oauth/complete`,
    method: 'POST',
    target: 'workflow',
    body: { code, state },
  });
  if (response?.detail || !response?.data?.connection) {
    throw failed(response, 'The sign-in could not be completed');
  }
  return response.data.connection;
};

export const savePersonalApiKey = async (
  orgId: string,
  provider: string,
  apiKey: string,
): Promise<PersonalConnection> => {
  const response = await apiRequest<{ connection: PersonalConnection }>({
    url: `${base(orgId)}/${provider}/api-key`,
    method: 'POST',
    target: 'workflow',
    body: { api_key: apiKey },
  });
  if (response?.detail || !response?.data?.connection) {
    throw failed(response, 'The key could not be saved');
  }
  return response.data.connection;
};

export const testPersonalConnection = async (
  orgId: string,
  provider: string,
): Promise<TestResult> => {
  const response = await apiRequest<TestResult>({
    url: `${base(orgId)}/${provider}/test`,
    method: 'POST',
    target: 'workflow',
  });
  if (response?.detail || !response?.data) {
    throw failed(response, 'The check could not run');
  }
  return response.data;
};

export const disconnectPersonalConnection = async (
  orgId: string,
  provider: string,
  pendingOnly = false,
): Promise<DisconnectResult> => {
  const response = await apiRequest<DisconnectResult>({
    url: `${base(orgId)}/${provider}${pendingOnly ? '?pending_only=true' : ''}`,
    method: 'DELETE',
    target: 'workflow',
  });
  if (response?.detail || !response?.data) {
    throw failed(response, 'Could not disconnect');
  }
  return response.data;
};
